// Mandate Service - API endpoints for mandate (mandato con representación) system

import { api } from './api';
import type {
  MandateSignRequest,
  MandateSignResponse,
  MandateStatusResponse,
} from '@/types/mandate';

export const mandateService = {
  /**
   * Firmar mandato - Envía firma manuscrita y genera el mandato
   * POST /hiring/{code}/mandate/sign
   */
  async signMandate(
    code: string,
    signatureBase64: string,
    accepted: boolean
  ): Promise<MandateSignResponse> {
    const request: MandateSignRequest = {
      hiring_code: code,
      signature_base64: signatureBase64,
      accepted,
    };
    const { data } = await api.post<MandateSignResponse>(
      `/hiring/${code}/mandate/sign`,
      request
    );
    return data;
  },

  /**
   * Subir PDF del mandato firmado al backend
   * POST /hiring/{code}/mandate/upload
   */
  async uploadMandatePDF(code: string, formData: FormData): Promise<{ status: string; message: string; pdf_url: string }> {
    const { data } = await api.post<{ status: string; message: string; pdf_url: string }>(
      `/hiring/${code}/mandate/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data;
  },

  /**
   * Obtener estado del mandato
   * GET /hiring/{code}/mandate
   */
  async getMandateStatus(code: string): Promise<MandateStatusResponse> {
    const { data } = await api.get<MandateStatusResponse>(
      `/hiring/${code}/mandate`
    );
    return data;
  },

  /**
   * Descargar PDF del mandato firmado
   * GET /hiring/{code}/mandate/download
   */
  async downloadMandate(code: string): Promise<Blob> {
    const { data } = await api.get<Blob>(
      `/hiring/${code}/mandate/download`,
      {
        responseType: 'blob',
        maxRedirects: 5,
      }
    );
    return data;
  },

  /**
   * Descargar mandato y disparar descarga en el navegador (endpoint público /hiring)
   */
  async downloadMandateFile(code: string, filename?: string): Promise<void> {
    const blob = await this.downloadMandate(code);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `mandato-migro-${code}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Descargar mandato vía CRM (JWT) por código de contrato y holder_index o mandate_code.
   * GET /crm/contracts/{code}/mandate/download?holder_index=0|1 o ?mandate_code=XXX
   * En 404 lanza con mensaje para mostrar en toast.
   */
  async downloadMandateCRM(
    contractCode: string,
    options?: { holder_index?: number; mandate_code?: string }
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (options?.mandate_code) {
      params.set('mandate_code', options.mandate_code);
    } else if (options?.holder_index !== undefined) {
      params.set('holder_index', String(options.holder_index));
    }
    const query = params.toString();
    const url = `/crm/contracts/${contractCode}/mandate/download${query ? `?${query}` : ''}`;
    try {
      const { data } = await api.get<Blob>(url, { responseType: 'blob', maxRedirects: 5 });
      return data;
    } catch (err: unknown) {
      const e = err as { response?: { status?: number }; message?: string };
      if (e?.response?.status === 404) {
        const msg = 'Mandato firmado no disponible.';
        throw Object.assign(new Error(msg), { response: e.response, status: 404 });
      }
      throw err;
    }
  },

  /**
   * Revocar mandato (solo admin)
   * PATCH /crm/contracts/{code}/mandate/revoke
   */
  async revokeMandate(code: string): Promise<{ status: string; message: string; mandate_status: string }> {
    const { data } = await api.patch<{ status: string; message: string; mandate_status: string }>(
      `/crm/contracts/${code}/mandate/revoke`
    );
    return data;
  },

  /**
   * Generar mandato con firma manuscrita (solo admin)
   * POST /crm/contracts/{code}/mandate/generate
   */
  async generateMandate(
    code: string,
    signatureBase64: string
  ): Promise<{
    status: string;
    message: string;
    mandate_pdf_url: string;
    mandate_status: string;
    mandate_signed_at: string;
    hiring_code: string;
  }> {
    const { data } = await api.post(
      `/crm/contracts/${code}/mandate/generate`,
      {
        signature_base64: signatureBase64,
        accepted_at: new Date().toISOString(),
      }
    );
    return data;
  },
};
